import { GrantTypeOfferCombinationModel } from '../../../../api-service/clients';
import { formatDate, today, toStartOfDay } from '../../../../utilities';

export const GRANT_TYPE_OFFER_COMBO_GROUP_SELECTABLE = 'selectable';
export const GRANT_TYPE_OFFER_COMBO_GROUP_NO_SEATS = 'noSeats';
export const GRANT_TYPE_OFFER_COMBO_GROUP_INVALID_VALIDITY = 'invalidValidity';

export function hasInvalidValidityPeriod(
  combination: GrantTypeOfferCombinationModel,
  referenceDate: Date = today()
): boolean {
  if (combination.endDate != null) {
    return true;
  }
  if (combination.startDate != null) {
    const start = toStartOfDay(combination.startDate);
    if (start && start.getTime() > referenceDate.getTime()) {
      return true;
    }
  }
  return false;
}

export function isGrantTypeOfferCombinationSelectable(
  combination: GrantTypeOfferCombinationModel
): boolean {
  if (hasInvalidValidityPeriod(combination)) {
    return false;
  }
  return (
    (combination.availableSeats ?? 0) > 0 ||
    combination.allowOverbooking === true
  );
}

function buildGrantTypeOfferSeatSuffix(
  combination: GrantTypeOfferCombinationModel
): string {
  const seats =
    combination.availableSeats != null ? combination.availableSeats : '?';
  const seatPart = `${seats} ledige pladser`;
  const overbookingNote =
    combination.allowOverbooking === true &&
    (combination.availableSeats ?? 0) <= 0
      ? ' (tillader overtegning)'
      : '';
  return `${seatPart}${overbookingNote}`;
}

/** Format the validity range for invalid-validity rows. Uses the standard dd-MM-yyyy area format.
 *  A missing start or end is rendered as "ingen startdato" / "ingen slutdato". */
export function formatGrantTypeOfferValidityRange(
  combination: GrantTypeOfferCombinationModel
): string {
  const start = combination.startDate
    ? formatDate(combination.startDate)
    : 'ingen startdato';
  const end = combination.endDate
    ? formatDate(combination.endDate)
    : 'ingen slutdato';
  return `Gyldighedsperiode: ${start} – ${end}`;
}

/** Single line for the closed input and Autocomplete filtering. */
export function formatGrantTypeOfferCombinationLabel(
  combination: GrantTypeOfferCombinationModel
): string {
  const seatSuffix = buildGrantTypeOfferSeatSuffix(combination);
  return `${combination.grantTypeName} — ${combination.offerTitle} (${combination.offerNumber}) - ${seatSuffix}`;
}

/** Second line in dropdown: offer + number + seats (indented under grant type).
 *  For invalid-validity rows the start/end dates are appended on a separate line. */
export function formatGrantTypeOfferCombinationSecondaryLine(
  combination: GrantTypeOfferCombinationModel
): string {
  const seatSuffix = buildGrantTypeOfferSeatSuffix(combination);
  const baseLine = `${combination.offerTitle ?? ''} (${
    combination.offerNumber
  }) - ${seatSuffix}`;
  if (hasInvalidValidityPeriod(combination)) {
    return `${baseLine}\n${formatGrantTypeOfferValidityRange(combination)}`;
  }
  return baseLine;
}

function groupRank(combination: GrantTypeOfferCombinationModel): number {
  const group = getGrantTypeOfferCombinationGroup(combination);
  if (group === GRANT_TYPE_OFFER_COMBO_GROUP_SELECTABLE) return 0;
  if (group === GRANT_TYPE_OFFER_COMBO_GROUP_NO_SEATS) return 1;
  return 2; // invalidValidity last
}

/** Selectable options first, then no-seats, then invalid-validity. Stable relative order within each group. */
export function sortGrantTypeOfferCombinationsDisabledLast(
  combinations: GrantTypeOfferCombinationModel[]
): GrantTypeOfferCombinationModel[] {
  return [...combinations].sort((a, b) => groupRank(a) - groupRank(b));
}

export function getGrantTypeOfferCombinationGroup(
  option: GrantTypeOfferCombinationModel
): string {
  if (hasInvalidValidityPeriod(option)) {
    return GRANT_TYPE_OFFER_COMBO_GROUP_INVALID_VALIDITY;
  }
  if ((option.availableSeats ?? 0) === 0 && option.allowOverbooking !== true) {
    return GRANT_TYPE_OFFER_COMBO_GROUP_NO_SEATS;
  }
  return GRANT_TYPE_OFFER_COMBO_GROUP_SELECTABLE;
}
